import React from "react";
import { ZoomIn, Award } from "lucide-react";

const CertificateCard = ({ certificate, onClick, isActive, cardRef }) => {
  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      aria-label={`View full certificate: ${certificate.title}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`group relative flex flex-col flex-shrink-0 w-[280px] sm:w-[320px] md:w-[340px] h-[440px] sm:h-[470px] rounded-2xl overflow-hidden cursor-pointer select-none transition-all duration-300 focus-visible:ring-2 focus-visible:ring-[#27B8FF] focus-visible:outline-none ${
        isActive
          ? "border-2 border-[#7C4DFF] shadow-[0_0_35px_rgba(124,77,255,0.35)] scale-[1.02]"
          : "border border-white/10 hover:border-[#7C4DFF]/60 hover:shadow-[0_12px_35px_rgba(124,77,255,0.22)] hover:-translate-y-1.5"
      }`}
      style={{
        background: "linear-gradient(180deg, rgba(15, 23, 48, 0.9) 0%, rgba(9, 14, 32, 0.95) 100%)",
      }}
    >
      {/* Top Header Badge */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 z-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#27B8FF] bg-[#27B8FF]/10 px-2.5 py-1 rounded-full border border-[#27B8FF]/20">
          <Award className="w-3 h-3 text-[#27B8FF]" />
          {certificate.badge}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 opacity-70 group-hover:opacity-100 group-hover:text-[#27B8FF] transition-opacity">
          <ZoomIn className="w-3.5 h-3.5" />
        </span>
      </div>

      {/* Certificate Image Frame */}
      <div className="relative flex-1 w-full p-3.5 flex items-center justify-center bg-slate-950/60 overflow-hidden">
        {/* Subtle grid pattern in image preview container */}
        <div className="absolute inset-0 tech-grid opacity-10 pointer-events-none" />

        <img
          src={certificate.image}
          alt={certificate.title}
          loading="lazy"
          className="max-h-full max-w-full object-contain rounded drop-shadow-lg transition-transform duration-300 group-hover:scale-[1.03]"
        />
      </div>

      {/* Bottom Information Panel with Gradient Overlay */}
      <div className="relative p-4 sm:p-5 bg-gradient-to-t from-[#060B1F] via-[#0B1226]/95 to-[#0B1226]/85 border-t border-white/10 z-10">
        <h3
          className="text-white font-bold text-sm sm:text-base leading-snug mb-1 truncate group-hover:text-ai-glow transition-colors"
          style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          title={certificate.title}
        >
          {certificate.title}
        </h3>

        <p className="text-[11px] sm:text-xs text-[#27B8FF] font-medium mb-2 truncate" title={certificate.issuer}>
          {certificate.issuer}
        </p>

        <p className="text-xs text-slate-300 leading-relaxed line-clamp-2">
          {certificate.description}
        </p>
      </div>
    </div>
  );
};

export default React.memo(CertificateCard);
